import {
  AlertTriangle,
  ArrowLeft,
  Check,
  FileText,
  FlaskConical,
  HeartPulse,
  Paperclip,
  PenLine,
  Save,
  ScrollText,
  ShieldAlert,
  Syringe,
  X,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { Logo } from '@/components/brand/Logo';
import {
  Badge,
  Button,
  ChipToggle,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  openAttachment,
  useConsultationOptions,
  useEncounter,
  useEncounterActions,
  type Encounter,
} from '@/features/encounters/api';
import {
  useEncounterLabOrders,
  useLabActions,
  usePrescriptionActions,
} from '@/features/orders/api';
import { usePatientActions } from '@/features/patients/api';
import { useQueueActions } from '@/features/queue/api';
import { isApiError } from '@/lib/api';
import { klTime } from '@/pages/calendar/time';
import { AllergyList } from '@/pages/patients/AllergyList';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import { EkycPhoto } from '@/pages/queue/EkycPhoto';
import { VitalsDrawer } from '@/pages/queue/VitalsDrawer';
import { VitalsSummary } from '@/pages/queue/VitalsSummary';
import { DocumentList } from './DocumentList';
import { LabOrderHandoff } from './LabOrderHandoff';
import { RxSection } from './RxSection';
import { NOTE_TEMPLATES, templateNotesAction } from './templates';
import { flushNotes, notesSettled, trackNotesSave } from './notes-autosave';
import { VisitForm } from './VisitForms';
import { VisitHistoryPanel } from './VisitHistoryPanel';
import {
  FORM_TITLES,
  consultationMissing,
  useLivePrescription,
  type FormKind,
} from './consult-utils';
import styles from './consult.module.css';
import { followUpLine } from '@/features/encounters/follow-up-line';
import { PatientSays } from '@/features/queue/PatientSays';
import { AppConsentForms } from './AppConsentForms';

/**
 * Admin v2 Doctor "Consultation" (#69–#72): a dedicated full-page clinical
 * workspace. It intentionally sits outside AppShell so the sidebar and the
 * underlying My patients view cannot remain behind the consultation.
 */
export function ConsultationPage() {
  const { id } = useParams();
  const { hasPermission } = useSession();
  const navigate = useNavigate();
  const exit = () => {
    if (hasPermission('my_patients:read')) void navigate('/my-patients');
    else void navigate(-1);
  };
  return <EncounterPage id={id} onExit={exit} />;
}

function EncounterPage({
  id,
  onExit,
}: {
  id: string | undefined;
  onExit: () => void;
}) {
  const { t } = useTranslation();
  const { data: e, isLoading, error, refetch } = useEncounter(id);
  const [form, setForm] = useState<FormKind | null>(null);
  const name = e?.patient.fullName ?? '';
  const back = () => {
    if (form) setForm(null);
    else onExit();
  };
  const title = form ? FORM_TITLES[form] : t('consultation.title');
  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.topbarIdentity}>
          <button
            type="button"
            className={styles.backButton}
            aria-label={
              form
                ? t('consultation.backToConsultation')
                : t('consultation.backToPatients')
            }
            onClick={back}
          >
            <ArrowLeft size={18} />
          </button>
          <Logo className={styles.logo} aria-hidden="true" />
          <div className={styles.headerCopy}>
            <div className={styles.titleLine}>
              <h1>{title}</h1>
              {e && (
                <Badge tone={e.status === 'open' ? 'warning' : 'success'}>
                  {e.status === 'open'
                    ? t('consultation.statusOpen')
                    : t('consultation.statusClosed')}
                </Badge>
              )}
            </div>
            <p>
              {e ? `${name} · ${e.branch.name}` : t('consultation.loading')}
            </p>
          </div>
        </div>
      </header>

      <main className={styles.page} id="main" tabIndex={-1}>
        <section className={styles.workspace} aria-label={title}>
          {isLoading && <LoadingState />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}
          {e && form && (
            <VisitForm kind={form} encounter={e} onBack={() => setForm(null)} />
          )}
          {e && !form && (
            <div className={styles.withHistory}>
              <EncounterBody
                key={`${e.id}:${e.status}`}
                encounter={e}
                onForm={setForm}
                onReload={() => void refetch()}
              />
              <VisitHistoryPanel
                patientId={e.patient.id}
                currentEncounterId={e.id}
                allergies={e.patient.allergies}
              />
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function EncounterBody({
  encounter: e,
  onForm,
  onReload,
}: {
  encounter: Encounter;
  onForm: (f: FormKind) => void;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const { me, hasPermission } = useSession();
  const navigate = useNavigate();
  const a = useEncounterActions(e.id);
  const qc = useQueryClient();
  const rx = usePrescriptionActions(e.id);
  const live = useLivePrescription(e.id);
  const {
    data: labOrders,
    isLoading: labOrdersLoading,
    error: labOrdersError,
    refetch: refetchLabOrders,
  } = useEncounterLabOrders(e.id);
  const lab = useLabActions();
  const q = useQueueActions();
  const patientActions = usePatientActions();
  const { data: opts } = useConsultationOptions();
  const toast = useToast();
  const mine = e.doctor.id === me?.id;
  const editable = e.status === 'open' && mine;
  const [notes, setNotes] = useState(e.notes);
  const [pendingTemplate, setPendingTemplate] = useState<
    (typeof NOTE_TEMPLATES)[number] | null
  >(null);
  const [advice, setAdvice] = useState(e.patientAdvice ?? '');
  const adviceValue = advice.trim() || null;
  const adviceDirty = adviceValue !== (e.patientAdvice ?? null);
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [addendum, setAddendum] = useState('');
  const [retake, setRetake] = useState(false);
  const [allergy, setAllergy] = useState({ substance: '', group: '' });
  const [procedure, setProcedure] = useState('');
  const dirty = notes !== e.notes;
  const draftLab = labOrders?.find((o) => o.status === 'draft') ?? null;
  const sentTests = new Set(
    (labOrders ?? [])
      .filter((o) => o.status !== 'draft' && o.status !== 'cancelled')
      .flatMap((o) => o.tests.map((t) => t.testName)),
  );
  const draftTests = new Map(
    draftLab?.tests.map((t) => [t.testName, t.id]) ?? [],
  );
  const rxDraft = live?.status === 'draft' && live.items.length > 0;
  const hasDraftOrders = rxDraft || (draftLab?.tests.length ?? 0) > 0;
  const signed = live?.status === 'signed';
  const missing = consultationMissing(e, live);

  const fail = (err: unknown) => {
    const conflict = isApiError(err) && err.code === 'VERSION_CONFLICT';
    toast.show({
      tone: 'error',
      title: conflict
        ? 'Notes changed elsewhere'
        : isApiError(err)
          ? (err.problem?.title ?? 'Not done')
          : 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
      onRetry: onReload,
    });
    if (conflict) onReload();
  };

  async function saveNotes(): Promise<number> {
    // The version is the encounter's as last answered by the API.
    await notesSettled(e.id);
    const cached = qc.getQueryData<Encounter>(['encounter', e.id]) ?? e;
    if (notes === cached.notes) return cached.version;
    const r = await trackNotesSave(
      e.id,
      a.saveNotes.mutateAsync({ version: cached.version, notes }),
    );
    setNotesSavedAt(new Date().toISOString());
    setNotesFailed(null);
    failedFor.current = null;
    return r.version;
  }

  // RL-1836 (decision D12): notes save themselves 2 seconds after typing
  // stops and when the consultation is left; a failed save is shown, never
  // lost, and is tried again only after more typing (or Try again).
  const [notesSavedAt, setNotesSavedAt] = useState<string | null>(null);
  const [notesFailed, setNotesFailed] = useState<string | null>(null);
  const notesSaving = a.saveNotes.isPending;
  const failedFor = useRef<string | null>(null);
  const autosave = useRef(saveNotes);
  const unsaved = useRef({ dirty: false, notes, id: e.id });
  useEffect(() => {
    autosave.current = saveNotes;
    unsaved.current = { dirty: editable && dirty, notes, id: e.id };
  });
  useEffect(() => {
    if (!editable || !dirty || notesSaving) return;
    if (failedFor.current === notes) return;
    const timer = window.setTimeout(() => {
      autosave.current().catch((err: unknown) => {
        failedFor.current = notes;
        setNotesFailed(
          isApiError(err) && err.code === 'VERSION_CONFLICT'
            ? 'Notes changed elsewhere. Copy your text, then reload the page.'
            : 'Notes not saved.',
        );
      });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [editable, dirty, notes, notesSaving]);
  useEffect(() => {
    const flush = () => {
      const u = unsaved.current;
      if (u.dirty) flushNotes(qc, u.id, u.notes);
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [qc]);

  /** "Sign prescription": signs the e-Rx; orders go to the lab and the Rx to the dispensary. */
  async function signAll() {
    try {
      await saveNotes();
      if (live && rxDraft) {
        const p = await rx.sign.mutateAsync({
          id: live.id,
          version: live.version,
        });
        toast.show({
          tone: 'success',
          title: `Prescription signed · ${p.number}`,
        });
      }
      if (draftLab && draftLab.tests.length) {
        const o = await lab.send.mutateAsync({
          id: draftLab.id,
          version: draftLab.version,
        });
        toast.show({
          tone: 'success',
          title: `Sent to the lab desk · ${o.number}`,
        });
      }
    } catch (err) {
      fail(err);
    }
  }

  /** Closing builds the bill, books any follow-up and issues the MC. */
  async function closeEncounter() {
    try {
      const v = await saveNotes();
      const closed = await a.close.mutateAsync({
        version: v,
        patientAdvice: adviceValue,
      });
      // RL-1697: say exactly what happened to the follow-up.
      const fu = followUpLine(closed.followUp);
      toast.show({
        tone: fu?.tone === 'warn' ? 'info' : 'success',
        title: t('consultation.closed'),
        body: [
          'The bill is built for the front desk; any MC is issued.',
          fu?.text,
        ]
          .filter(Boolean)
          .join(' '),
      });
    } catch (err) {
      fail(err);
    }
  }

  const v = e.vitals ?? e.vitalsHistory[0] ?? null;
  const followUp = followUpLine(e.followUp);
  const verifiedPayer =
    e.cover?.status === 'covered' ? (
      <Badge tone="success">verified</Badge>
    ) : e.cover ? (
      <Badge tone="danger">not covered</Badge>
    ) : null;

  return (
    <div className={styles.stack}>
      <Link className={styles.recordButton} to={`/patients/${e.patient.id}`}>
        <ScrollText size={16} /> Open full record: visits, results, documents
      </Link>

      <div className={styles.headline}>
        <EkycPhoto
          name={e.patient.fullName}
          verifiedAt={e.patient.ekycVerifiedAt}
        />
        <div>
          <p className={styles.name}>{e.patient.fullName}</p>
          <p className={styles.sub}>
            Check the face matches the person in the room
          </p>
        </div>
      </div>
      <PatientSays reason={e.patientReason} />
      <dl className={styles.rows}>
        <dt>
          {e.patient.identity?.type === 'passport' ? 'Passport' : 'MyKad'}
        </dt>
        <dd className={styles.mono}>
          <PatientIdentityText identity={e.patient.identity} />
        </dd>
        <dt>Payer</dt>
        <dd>
          {e.payer?.name ?? 'Self'} {verifiedPayer}
        </dd>
        <dt>Allergies</dt>
        <dd>
          <AllergyList
            allergies={e.patient.allergies}
            patientId={e.patient.id}
            onRemove={
              editable
                ? (x) =>
                    x.id &&
                    patientActions.removeAllergy
                      .mutateAsync({ id: e.patient.id, allergyId: x.id })
                      .catch(fail)
                : undefined
            }
          />
        </dd>
        <dt>Queue</dt>
        <dd>
          {e.queueTicket
            ? `${e.queueTicket.number} · ${e.queueTicket.status.replace('_', ' ')}${e.queueTicket.room ? ` · ${e.queueTicket.room}` : ''}`
            : '—'}
        </dd>
      </dl>
      {editable && (
        <div
          className={styles.inlineForm}
          role="group"
          aria-label="New allergy"
        >
          <Input
            aria-label="Allergy"
            placeholder="Add allergy (e.g. Penicillin)"
            value={allergy.substance}
            onChange={(x) =>
              setAllergy({ ...allergy, substance: x.target.value })
            }
          />
          <Select
            aria-label="Allergy group"
            value={allergy.group}
            onChange={(x) => setAllergy({ ...allergy, group: x.target.value })}
            options={[
              { value: '', label: 'Group (optional)' },
              { value: 'penicillin', label: 'Penicillin' },
              { value: 'nsaid', label: 'NSAIDs' },
              { value: 'sulfonamide', label: 'Sulfonamides' },
              { value: 'benzodiazepine', label: 'Benzodiazepines' },
            ]}
          />
          <Button
            size="sm"
            aria-label="Add allergy"
            disabled={allergy.substance.trim().length < 2}
            loading={patientActions.addAllergy.isPending}
            onClick={() =>
              patientActions.addAllergy
                .mutateAsync({
                  id: e.patient.id,
                  substance: allergy.substance.trim(),
                  allergyGroup: allergy.group || undefined,
                })
                .then(() => setAllergy({ substance: '', group: '' }))
                .catch(fail)
            }
          >
            Add
          </Button>
        </div>
      )}

      {e.notifiable.length > 0 && (
        <p className={styles.blockNote} role="alert">
          <AlertTriangle size={14} /> Notifiable disease:{' '}
          {e.notifiable.map((n) => `${n.disease} (${n.code})`).join(', ')}.
          Notify the District Health Office (CDCIS e-Notification); a reminder
          is in the bell.
        </p>
      )}

      <VitalsSummary
        vitals={v}
        action={
          editable &&
          e.queueTicket &&
          hasPermission('vitals:record') && (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setRetake(true)}
            >
              Retake
            </button>
          )
        }
      />

      {editable && (
        <div className={styles.section}>
          <p className="eyebrow">Start from a template</p>
          <div className={styles.chipRow}>
            {NOTE_TEMPLATES.map((t) => (
              <button
                key={t.key}
                type="button"
                className={styles.chip}
                onClick={() => {
                  if (templateNotesAction(notes, t.text) === 'set')
                    setNotes(t.text);
                  else setPendingTemplate(t);
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <Dialog
        open={pendingTemplate !== null}
        onClose={() => setPendingTemplate(null)}
        variant="modal"
        title="Replace your notes?"
        footer={
          <>
            <Button onClick={() => setPendingTemplate(null)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => {
                if (pendingTemplate) setNotes(pendingTemplate.text);
                setPendingTemplate(null);
              }}
            >
              Replace notes
            </Button>
          </>
        }
      >
        <p>
          The notes already contain text you wrote. Starting from the{' '}
          {pendingTemplate?.label} template replaces it. Cancel to keep your
          notes.
        </p>
      </Dialog>
      <Field label="Notes">
        <textarea
          className={styles.notes}
          value={notes}
          onChange={(x) => setNotes(x.target.value)}
          readOnly={!editable}
          placeholder={'S:\nO:\nA:\nP:'}
          rows={7}
        />
      </Field>

      <div className={styles.section}>
        <p className="eyebrow">Diagnosis (ICD-10)</p>
        {e.diagnoses.length === 0 && (
          <p className={styles.sub}>
            No diagnosis yet. At least one is needed to close.
          </p>
        )}
        <ul className={styles.dxList}>
          {e.diagnoses.map((d) => (
            <li key={d.id}>
              <span className={styles.mono}>{d.code}</span> {d.description}{' '}
              {d.isPrimary && <Badge tone="brand">Primary</Badge>}
              {editable && (
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label={`Remove ${d.code}`}
                  onClick={() =>
                    a.removeDiagnosis.mutateAsync(d.id).catch(fail)
                  }
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
        {editable && (
          <form
            className={styles.inlineForm}
            onSubmit={(ev) => {
              ev.preventDefault();
              void a.addDiagnosis
                .mutateAsync({
                  code: code.trim(),
                  description: description.trim(),
                })
                .then(() => {
                  setCode('');
                  setDescription('');
                })
                .catch(fail);
            }}
          >
            <Input
              aria-label="ICD-10 code"
              placeholder="A90"
              value={code}
              onChange={(x) => setCode(x.target.value.toUpperCase())}
            />
            <Input
              aria-label="Diagnosis description"
              placeholder="Dengue fever"
              value={description}
              onChange={(x) => setDescription(x.target.value)}
            />
            <Button
              type="submit"
              size="sm"
              aria-label="Add diagnosis"
              disabled={!code.trim() || description.trim().length < 2}
              loading={a.addDiagnosis.isPending}
            >
              Add
            </Button>
          </form>
        )}
        <div className={styles.aiBox}>
          {e.aiConsent.granted ? (
            <p>
              AI code suggestions: consent recorded. No AI service is connected
              on staging, so no suggestions are shown.
            </p>
          ) : (
            <>
              <p>
                AI code suggestions are off for this patient: no AI consent
                recorded.
              </p>
              {editable && (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={a.aiConsent.isPending}
                  onClick={() => a.aiConsent.mutateAsync(true).catch(fail)}
                >
                  Record consent
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <RxSection encounterId={e.id} editable={editable} />

      {e.status === 'closed' && (
        <LabOrderHandoff
          orders={labOrders}
          isLoading={labOrdersLoading}
          error={labOrdersError}
          onRetry={() => void refetchLabOrders()}
        />
      )}

      <section className={styles.section} aria-label="Procedures">
        <p className="eyebrow">Procedures</p>
        {e.procedures.map((p) => (
          <p key={p.id} className={styles.procLine}>
            <span>
              <strong>{p.name}</strong>
              {p.consumables ? `: ${p.consumables}` : ''}
            </span>
            {p.consent === 'needed' && (
              <Badge tone="danger">Written consent needed</Badge>
            )}
            {p.consent === 'signed' && (
              <Badge tone="success">Consent signed</Badge>
            )}
            {editable && p.consent === 'needed' && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => a.consentProcedure.mutateAsync(p.id).catch(fail)}
              >
                Get consent
              </button>
            )}
            {editable && (
              <button
                type="button"
                className={styles.iconButton}
                aria-label={`Remove ${p.name}`}
                onClick={() => a.removeProcedure.mutateAsync(p.id).catch(fail)}
              >
                <X size={14} />
              </button>
            )}
          </p>
        ))}
        <AppConsentForms
          encounterId={e.id}
          forms={e.procedureConsents ?? []}
          editable={editable}
          onError={fail}
        />
        {editable && (
          <div className={styles.inlineForm}>
            <Select
              aria-label="Procedure"
              value={procedure}
              onChange={(x) => setProcedure(x.target.value)}
              placeholder="Add a procedure"
              options={(opts?.procedures ?? []).map((p) => ({
                value: p.code,
                label: `${p.name}${p.consentRequired ? ' · consent needed' : ''}`,
              }))}
            />
            <Button
              size="sm"
              aria-label="Add procedure"
              disabled={!procedure}
              loading={a.addProcedure.isPending}
              onClick={() =>
                a.addProcedure
                  .mutateAsync(procedure)
                  .then(() => setProcedure(''))
                  .catch(fail)
              }
            >
              Add
            </Button>
          </div>
        )}
      </section>

      {editable && (
        <section className={styles.section} aria-label="More for this visit">
          <p className={styles.sectionTitle}>More for this visit</p>
          <div className={styles.moreGrid}>
            <div>
              <p className="eyebrow">Tests and vaccines</p>
              <MoreButton
                icon={<FlaskConical size={14} />}
                label="In-clinic test"
                onClick={() => onForm('in-clinic-test')}
              />
              <MoreButton
                icon={<Syringe size={14} />}
                label="Vaccination"
                onClick={() => onForm('vaccination')}
              />
              <MoreButton
                icon={<HeartPulse size={14} />}
                label="Depression check (PHQ-9)"
                onClick={() => onForm('phq9')}
              />
            </div>
            <div>
              <p className="eyebrow">Documents</p>
              <MoreButton
                icon={<FileText size={14} />}
                label="Referral letter"
                onClick={() => void navigate(`/consultations/${e.id}/referral`)}
              />
              <MoreButton
                icon={<FileText size={14} />}
                label="X-ray or scan"
                onClick={() => onForm('imaging')}
              />
              <MoreButton
                icon={<FileText size={14} />}
                label="Time slip"
                onClick={() => onForm('time-slip')}
              />
              {signed && (
                <MoreButton
                  icon={<FileText size={14} />}
                  label="Prescription for a pharmacy"
                  onClick={() =>
                    a.submit
                      .mutateAsync({ path: 'outside-prescriptions' })
                      .then(() =>
                        toast.show({
                          tone: 'success',
                          title: 'Prescription for a pharmacy issued (QR)',
                        }),
                      )
                      .catch(fail)
                  }
                />
              )}
              <MoreButton
                icon={<Paperclip size={14} />}
                label="Attach photo or file"
                onClick={() => onForm('attach')}
              />
            </div>
            <div>
              <p className="eyebrow">Safety</p>
              <MoreButton
                icon={<ShieldAlert size={14} />}
                label="Adverse drug reaction"
                onClick={() => onForm('adr')}
              />
              <MoreButton
                icon={<ShieldAlert size={14} />}
                label="Child protection concern"
                onClick={() => onForm('child')}
              />
              <MoreButton
                icon={<ShieldAlert size={14} />}
                label="Domestic violence"
                onClick={() => onForm('dv')}
              />
              <MoreButton
                icon={<HeartPulse size={14} />}
                label="Emergency"
                danger
                onClick={() => onForm('emergency')}
              />
            </div>
          </div>

          {editable && (
            <>
              <p className={styles.sectionTitle}>Orders</p>
              <div className={styles.chipRow} role="group" aria-label="Orders">
                {(opts?.orders ?? []).map((t) => {
                  const on = draftTests.has(t) || sentTests.has(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      disabled={sentTests.has(t)}
                      className={[styles.chip, on && styles.chipOn]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() =>
                        draftTests.has(t)
                          ? lab.removeTest
                              .mutateAsync({
                                id: draftLab!.id,
                                itemId: draftTests.get(t)!,
                              })
                              .catch(fail)
                          : lab.addTest
                              .mutateAsync({ encounterId: e.id, testName: t })
                              .catch(fail)
                      }
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              {sentTests.size > 0 && (
                <p className={styles.sub}>
                  Sent to the lab desk: {[...sentTests].join(', ')}
                </p>
              )}
            </>
          )}

          <p className="eyebrow">Medical certificate</p>
          <ChipToggle
            label="Medical certificate"
            single
            options={[
              { value: '0', label: 'No MC' },
              { value: '1', label: '1 day' },
              { value: '2', label: '2 days' },
              { value: '3', label: '3 days' },
            ]}
            selected={[String(e.mcDays)]}
            onChange={([d]) =>
              a.setPlan.mutateAsync({ mcDays: Number(d) }).catch(fail)
            }
          />
          <p className={styles.sub}>
            Starts today, the day of this consultation. Backdating is not
            possible. The MC gets a QR code when the visit is closed.
          </p>
          <div className={styles.chipRow}>
            <button
              type="button"
              className={styles.chip}
              onClick={() => void navigate(`/consultations/${e.id}/referral`)}
            >
              Referral letter
            </button>
            <button
              type="button"
              role="checkbox"
              aria-checked={e.followUpMonths === 3}
              className={[styles.chip, e.followUpMonths === 3 && styles.chipOn]
                .filter(Boolean)
                .join(' ')}
              onClick={() =>
                a.setPlan
                  .mutateAsync({
                    followUpMonths: e.followUpMonths === 3 ? null : 3,
                  })
                  .catch(fail)
              }
            >
              Follow-up 3 months
            </button>
          </div>
        </section>
      )}

      {(e.records.length > 0 || e.restrictedHidden > 0) && (
        <section className={styles.section} aria-label="Saved for this visit">
          <p className="eyebrow">Saved for this visit</p>
          <ul className={styles.recordList}>
            {e.records.map((r) => (
              <li key={r.id}>
                <strong>{RECORD_LABEL[r.kind]}</strong> · {klTime(r.recordedAt)}
                {r.restricted && <Badge tone="warning">Restricted</Badge>}
                <br />
                <span className={styles.sub}>
                  {recordSummary(r.kind, r.data)}
                </span>
              </li>
            ))}
          </ul>
          {e.restrictedHidden > 0 && (
            <p className={styles.sub}>
              {e.restrictedHidden} restricted note(s) are hidden from your role.
            </p>
          )}
        </section>
      )}

      {e.documents.length > 0 && (
        <section className={styles.section} aria-label="Documents">
          <p className="eyebrow">Documents</p>
          <DocumentList documents={e.documents} />
        </section>
      )}
      {e.attachments.length > 0 && (
        <section className={styles.section} aria-label="Attachments">
          <p className="eyebrow">Attachments</p>
          {e.attachments.map((x) => (
            <button
              key={x.id}
              type="button"
              className={styles.linkButton}
              onClick={() => void openAttachment(x.id).catch(fail)}
            >
              <Paperclip size={14} /> {x.fileName}
            </button>
          ))}
        </section>
      )}
      {followUp && (
        <p
          className={followUp.tone === 'ok' ? styles.okNote : styles.warnNote}
          role="status"
        >
          {followUp.text}
        </p>
      )}

      {e.status === 'open' && !mine && (
        <p className={styles.warnNote}>
          This consultation belongs to {e.doctor.fullName}. You can read it but
          not change it.
        </p>
      )}

      {editable && (
        <section
          className={styles.section}
          aria-label={t('consultation.patientAdvice')}
        >
          <Field
            label={t('consultation.patientAdvice')}
            hint={t('consultation.patientAdviceHint')}
          >
            <textarea
              className={styles.notes}
              rows={3}
              maxLength={ADVICE_MAX}
              value={advice}
              onChange={(x) => setAdvice(x.target.value)}
              onBlur={(x) => {
                // Close sends the advice itself; saving it too would race it.
                const to = x.relatedTarget as HTMLElement | null;
                if (to?.dataset.closesEncounter) return;
                if (adviceDirty)
                  a.setPlan
                    .mutateAsync({ patientAdvice: adviceValue })
                    .catch(fail);
              }}
            />
          </Field>
          <p className={styles.sub} aria-live="polite">
            {advice.length} / {ADVICE_MAX}
          </p>
        </section>
      )}
      {e.status === 'closed' && (
        <section
          className={styles.section}
          aria-label={t('consultation.patientAdvice')}
        >
          <p className="eyebrow">{t('consultation.patientAdvice')}</p>
          <p className={styles.adviceText}>
            {e.patientAdvice ?? t('consultation.patientAdviceNone')}
          </p>
        </section>
      )}

      {editable && (
        <div className={styles.signArea}>
          {(missing.length > 0 || hasDraftOrders) && (
            <section
              className={styles.signChecklist}
              aria-label={t('consultation.checklistLabel')}
            >
              <p className="eyebrow">{t('consultation.beforeClose')}</p>
              <ul>
                {missing.map((item) => (
                  <li key={item.code}>
                    <strong>{item.label}</strong>
                    {item.detail ? ` ${item.detail}` : ''}
                  </li>
                ))}
                {hasDraftOrders && (
                  <li>
                    <strong>{t('consultation.finishDraftOrders')}</strong>
                  </li>
                )}
              </ul>
            </section>
          )}
          <div className={styles.footer}>
            {editable && (
              <span
                className={notesFailed ? styles.notesFailed : styles.sub}
                role="status"
              >
                {notesSaving
                  ? 'Saving notes…'
                  : notesFailed
                    ? notesFailed
                    : notesSavedAt
                      ? `Notes saved ${klTime(notesSavedAt)}`
                      : null}
                {notesFailed && !notesSaving && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Save size={16} />}
                    onClick={() => void saveNotes().catch(fail)}
                  >
                    Try again
                  </Button>
                )}
              </span>
            )}
            {hasDraftOrders || !signed ? (
              <Button
                variant="primary"
                icon={<PenLine size={16} />}
                disabled={!hasDraftOrders || !!live?.blocked}
                loading={rx.sign.isPending || lab.send.isPending}
                onClick={() => void signAll()}
              >
                Sign prescription
              </Button>
            ) : null}
            <Button
              variant="primary"
              icon={<Check size={16} />}
              disabled={missing.length > 0 || hasDraftOrders}
              loading={a.close.isPending}
              data-closes-encounter="true"
              onClick={() => void closeEncounter()}
            >
              {t('consultation.closeEncounter')}
            </Button>
          </div>
        </div>
      )}

      {e.status === 'closed' && (
        <section className={styles.section} aria-label="Addenda">
          <p className="eyebrow">Addenda</p>
          <p className={styles.sub}>
            Closed{' '}
            {e.closedAt ? new Date(e.closedAt).toLocaleString('en-GB') : ''}. A
            closed note only gets a dated addendum; the original stays readable.
          </p>
          <ul className={styles.addenda}>
            {e.addenda.map((ad) => (
              <li key={ad.id}>
                <p className={styles.sub}>
                  {new Date(ad.createdAt).toLocaleString('en-GB')} ·{' '}
                  {ad.author.fullName}
                </p>
                <p>{ad.body}</p>
              </li>
            ))}
          </ul>
          {hasPermission('encounters:write') && (
            <div className={styles.addendumForm}>
              <Field label="New addendum">
                <textarea
                  className={styles.notes}
                  rows={3}
                  value={addendum}
                  onChange={(x) => setAddendum(x.target.value)}
                />
              </Field>
              <Button
                size="sm"
                disabled={addendum.trim().length < 2}
                loading={a.addAddendum.isPending}
                onClick={() =>
                  a.addAddendum
                    .mutateAsync(addendum.trim())
                    .then(() => setAddendum(''))
                    .catch(fail)
                }
              >
                Add addendum
              </Button>
            </div>
          )}
        </section>
      )}

      <VitalsDrawer
        key={retake ? 'retake' : 'none'}
        ticket={
          retake && e.queueTicket
            ? {
                ticketId: e.queueTicket.id,
                number: e.queueTicket.number,
                service: e.queueTicket.service?.name ?? null,
                patientName: e.patient.fullName,
                dateOfBirth: e.patient.dateOfBirth,
                allergies: e.patient.allergies,
              }
            : null
        }
        busy={q.recordVitals.isPending}
        onClose={() => setRetake(false)}
        onSave={(vitals) =>
          e.queueTicket &&
          q.recordVitals
            .mutateAsync({ id: e.queueTicket.id, vitals })
            .then(() => {
              setRetake(false);
              onReload();
            })
            .catch(fail)
        }
      />
    </div>
  );
}

function MoreButton({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className={[styles.moreButton, danger && styles.moreDanger]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
    >
      {icon} {label}
    </button>
  );
}

/** RL-1956: the API keeps up to 1000 characters. */
const ADVICE_MAX = 1000;

const RECORD_LABEL = {
  in_clinic_test: 'In-clinic test',
  vaccination: 'Vaccination',
  phq9: 'PHQ-9',
  adverse_reaction: 'Adverse drug reaction',
  child_protection: 'Child protection concern',
  domestic_violence: 'Domestic violence',
  emergency_step: 'Emergency',
} as const;

function recordSummary(
  kind: keyof typeof RECORD_LABEL,
  d: Record<string, unknown>,
): string {
  const s = (k: string) => String(d[k] ?? '');
  switch (kind) {
    case 'in_clinic_test':
      return `${s('test')}: ${s('result')}`;
    case 'vaccination':
      return `${s('vaccine')} · dose ${s('dose')}${d.batchNumber || d.batch ? ` · batch ${String(d.batchNumber || d.batch)}` : ''} · observe ${s('observeMinutes')} min, until ${d.observeUntil ? klTime(s('observeUntil')) : '—'} · certificate ${s('certificateNumber')}`;
    case 'phq9':
      return `Score ${s('score')} (${s('band')})${d.crisis ? ' · question 9 positive' : ''}`;
    case 'adverse_reaction':
      return `${s('medicine')} · ${s('reaction').replace(/_/g, ' ')} · ${s('severity')} · NPRA report ${s('npraReport')}`;
    case 'child_protection':
      return `Restricted note · Social Welfare Officer: ${s('welfareInformedBy').replace(/_/g, ' ')}`;
    case 'domestic_violence':
      return `Restricted note · referral ${s('referral').replace(/_/g, ' ')} · ${d.safeContact === 'safe_only' ? 'safe contact only' : 'normal contact'}`;
    case 'emergency_step':
      return s('step').replace(/_/g, ' ');
  }
}
